import is from '@sindresorhus/is';
import { z } from 'zod';

import { type SkillProficiencyLevel, skillProficiencyLevelSchema } from '~/shared/data-contract/skill';
import { type LanguageCodeForJSA, languageCodeSchemaForJSA } from '~/shared/internationalization';

import {
    createUrlTileCodec,
    createUrlTileDefinition,
    serializeUrlTilePayloads,
    URL_TILE_SEARCH_PARAM,
    type UrlTileLegacyRewriter,
    type UrlTileModule,
    type UrlTilePayload,
    type UrlTileRenderContext,
    urlTileIntegerCodec,
    urlTileStringCodec,
} from '../url-tiles';
import { SkillSyncStatusAction } from './skill-details/SkillSyncStatusAction';
import { SkillsDetailsPanel } from './skill-details/SkillsDetailsPanel';

export const SKILL_DETAIL_TILE_SLOT_KEY = 'skill.detail';
export const SKILL_DETAIL_TILE_TYPE = 'v1.skill.detail';

const skillDetailTileSchema = z.object({
    preferredLanguage: languageCodeSchemaForJSA.optional(),
    skillId: z.uuid(),
    proficiency: skillProficiencyLevelSchema.optional(),
    type: z.literal(SKILL_DETAIL_TILE_TYPE),
});

export type SkillDetailTilePayload = z.infer<typeof skillDetailTileSchema>;

export const createSkillDetailTilePayload = ({
    preferredLanguage,
    skillId,
    proficiency,
}: {
    readonly preferredLanguage?: LanguageCodeForJSA;
    readonly skillId: string;
    readonly proficiency?: SkillProficiencyLevel;
}): SkillDetailTilePayload => ({
    preferredLanguage,
    skillId,
    proficiency,
    type: SKILL_DETAIL_TILE_TYPE,
});

export const selectSkillIdFromTile = (payload: UrlTilePayload): string | undefined =>
    is.string(payload['skillId']) ? payload['skillId'] : undefined;

const createSkillDetailTileProps = (payload: SkillDetailTilePayload, context: UrlTileRenderContext) => ({
    disableArrows: true,
    id: payload.skillId,
    onClose: () => context.closeTile(SKILL_DETAIL_TILE_SLOT_KEY),
    onNavigate: (skillId: string) => {
        context.updateTile(
            createSkillDetailTilePayload({
                preferredLanguage: payload.preferredLanguage,
                skillId,
                proficiency: payload.proficiency,
            }),
        );
    },
    onPreferredLanguageChange: (preferredLanguage: LanguageCodeForJSA) => {
        context.updateTile(
            createSkillDetailTilePayload({
                preferredLanguage,
                skillId: payload.skillId,
                proficiency: payload.proficiency,
            }),
        );
    },
    proficiency: payload.proficiency,
    preferredLanguage: payload.preferredLanguage,
});

export const skillDetailUrlTileDefinition = createUrlTileDefinition({
    codec: createUrlTileCodec<SkillDetailTilePayload>({
        options: [
            { codec: urlTileStringCodec, field: 'preferredLanguage', key: 'lang' },
            { codec: urlTileIntegerCodec, field: 'proficiency', key: 'proficiency' },
        ],
        segments: ['skillId'],
        type: SKILL_DETAIL_TILE_TYPE,
    }),
    maxWidth: '600px',
    minWidth: '600px',
    render: (props: ReturnType<typeof createSkillDetailTileProps>) => (
        <SkillsDetailsPanel {...props} actions={<SkillSyncStatusAction skillId={props.id} />} />
    ),
    schema: skillDetailTileSchema,
    slotKey: () => SKILL_DETAIL_TILE_SLOT_KEY,
    tileKey: (payload) => `${SKILL_DETAIL_TILE_SLOT_KEY}.${payload.skillId}`,
    toProps: createSkillDetailTileProps,
});

const skillDetailLegacySearchParamKeys = [
    'details.skills',
    'details.skills.id',
    'details.skills.preferredLanguage',
    // Old bookmarks may carry a proficiency; intentionally dropped — the panel derives it.
    'details.skills.proficiency',
] as const;

const hasSkillDetailLegacySearchParams = (searchParams: URLSearchParams): boolean => {
    return skillDetailLegacySearchParamKeys.some((key) => searchParams.has(key));
};

const deleteSkillDetailLegacySearchParams = (searchParams: URLSearchParams): void => {
    for (const key of skillDetailLegacySearchParamKeys) {
        searchParams.delete(key);
    }
};

const parseLegacySkillDetailPayloads = (searchParams: URLSearchParams): readonly SkillDetailTilePayload[] => {
    const skillDetailsType = searchParams.get('details.skills');
    const skillId = searchParams.get('details.skills.id');
    const preferredLanguage = searchParams.get('details.skills.preferredLanguage') ?? undefined;
    const parsedPayload = skillDetailTileSchema.safeParse({
        preferredLanguage,
        skillId,
        type: skillDetailsType === 'skills' ? SKILL_DETAIL_TILE_TYPE : undefined,
    });

    return parsedPayload.success ? [parsedPayload.data] : [];
};

export const skillDetailLegacyUrlTileRewriter: UrlTileLegacyRewriter = {
    rewrite: ({ definitions, hadCanonicalTileParams, searchParams }) => {
        if (!hasSkillDetailLegacySearchParams(searchParams)) {
            return undefined;
        }

        const nextParams = new URLSearchParams(searchParams);
        deleteSkillDetailLegacySearchParams(nextParams);

        if (hadCanonicalTileParams) {
            return nextParams;
        }

        for (const value of serializeUrlTilePayloads(parseLegacySkillDetailPayloads(searchParams), definitions)) {
            nextParams.append(URL_TILE_SEARCH_PARAM, value);
        }

        return nextParams;
    },
};

export const skillDetailUrlTileModule: UrlTileModule = {
    definitions: [skillDetailUrlTileDefinition],
    legacyRewriters: [skillDetailLegacyUrlTileRewriter],
};
